import type { RefObject } from 'react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

export type ChartSize = { readonly height: number; readonly width: number };

/** Tracks the host's CSS width so a chart can size its canvas to the pocket it sits in. */
const useElementWidth = (fallback: number): [RefObject<HTMLDivElement | null>, number] => {
    const ref = useRef<HTMLDivElement | null>(null);
    const [width, setWidth] = useState(fallback);

    useEffect(() => {
        const element = ref.current;

        if (element === null) {
            return;
        }

        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];

            if (entry !== undefined && entry.contentRect.width > 0) {
                setWidth(entry.contentRect.width);
            }
        });
        observer.observe(element);

        return () => observer.disconnect();
    }, []);

    return [ref, width];
};

const MIN_CHART_WIDTH = 360;

/** A chart pocket: the host ref and the canvas size that fills its width at a fixed height. */
export const useChartSize = (height: number): [RefObject<HTMLDivElement | null>, ChartSize] => {
    const [ref, width] = useElementWidth(720);
    const size = useMemo<ChartSize>(() => ({ height, width: Math.max(MIN_CHART_WIDTH, width) }), [height, width]);

    return [ref, size];
};

/**
 * Runs `draw` on a device-pixel-ratio aware 2D context whenever the size or the draw callback changes.
 * The callback receives CSS-pixel coordinates; the scale is applied once here.
 */
export const useChartCanvas = (
    size: ChartSize,
    draw: (context: CanvasRenderingContext2D, size: ChartSize) => void,
): RefObject<HTMLCanvasElement | null> => {
    const ref = useRef<HTMLCanvasElement | null>(null);

    useLayoutEffect(() => {
        const canvas = ref.current;
        const context = canvas?.getContext('2d');

        if (canvas === null || context === null || context === undefined) {
            return;
        }

        const ratio = globalThis.devicePixelRatio > 0 ? globalThis.devicePixelRatio : 1;
        canvas.width = Math.round(size.width * ratio);
        canvas.height = Math.round(size.height * ratio);
        canvas.style.width = `${size.width}px`;
        canvas.style.height = `${size.height}px`;
        context.setTransform(ratio, 0, 0, ratio, 0, 0);
        context.clearRect(0, 0, size.width, size.height);
        draw(context, size);
    }, [draw, size]);

    return ref;
};

/** Pointer position relative to the canvas, in CSS pixels. */
export const pointerPosition = (
    event: { readonly clientX: number; readonly clientY: number },
    canvas: HTMLCanvasElement,
): { readonly x: number; readonly y: number } => {
    const rect = canvas.getBoundingClientRect();

    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
};

export const truncateLabel = (context: CanvasRenderingContext2D, label: string, maxWidth: number): string => {
    if (context.measureText(label).width <= maxWidth) {
        return label;
    }

    let text = label;

    while (text.length > 1 && context.measureText(`${text}…`).width > maxWidth) {
        text = text.slice(0, -1);
    }

    return text.length <= 1 ? '' : `${text}…`;
};
